import { FC, useEffect, useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useAppDispatch } from '../../store/hooks';
import { fetchInvoicePaymentHistory } from '../../store/CashInflowStore/cashInflowThunk';
import { fetchPurchaseInvoicePaymentHistory } from '../../store/CashOutflowStore/cashOutflowThunk';
import { Utils } from '../../utils/common-utils';
import { PAYMENT_MODE_LABELS } from '../Cashflow/PaymentStatusTooltip';

export interface InvoicePaymentRow {
  id: string;
  date: any;
  receiptNo?: string | number;
  mode: string;
  amount: number;
  instrument?: string;
}

const modeLabel = (mode: string) => PAYMENT_MODE_LABELS[mode.toUpperCase()] || mode;

const BREAKDOWN_CLASS = 'invoice-payment-breakdown';

// Items with this class span both columns of the details grid.
export const FULL_ROW_CLASS = 'detail-full-row';

// Put on the item in the last column so it hugs the card's right edge (e.g. Invoice Type).
export const RIGHT_CELL_CLASS = 'detail-right-cell';

// Preview details card as three columns (Invoice Date | Payment Status | Invoice Type); full rows, dividers and the breakdown span all.
export const DETAIL_GRID_SX = {
  display: 'grid',
  // Content-sized columns with the spare width shared out equally, so both gaps match.
  gridTemplateColumns: 'repeat(3, auto)',
  justifyContent: 'space-between',
  columnGap: 1.5,
  rowGap: 1.5,
  // Narrow columns: long values (e.g. PARTIALLY_PAID) wrap inside their cell instead of overflowing.
  '& > *': { minWidth: 0, overflowWrap: 'anywhere' },
  [`& > .${FULL_ROW_CLASS}, & > hr, & > .${BREAKDOWN_CLASS}`]: { gridColumn: '1 / -1' },
  // The grid gap spaces the divider; its own margin doubled the gap.
  '& > hr': { margin: 0 },
  [`& > .${RIGHT_CELL_CLASS}`]: { textAlign: 'right' },
};

// Payments applied to one invoice (sale: payment receipts, purchase: vendor payments), oldest first.
export const useInvoicePayments = (invoiceId?: string, isPurchase = false, refreshKey?: any) => {
  const dispatch = useAppDispatch();
  const [payments, setPayments] = useState<InvoicePaymentRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!invoiceId) {
      setPayments([]);
      return;
    }
    let active = true;
    setLoading(true);
    const request: any = isPurchase
      ? dispatch(fetchPurchaseInvoicePaymentHistory(invoiceId))
      : dispatch(fetchInvoicePaymentHistory(invoiceId));
    Promise.resolve(request)
      .then((res: any) => {
        if (!active) return;
        const list: any[] = res?.payload?.payments || [];
        const rows = list.map((p: any) => {
          const parent = (p.paymentReceipt && typeof p.paymentReceipt === 'object') ? p.paymentReceipt
            : (p.paymentMade && typeof p.paymentMade === 'object') ? p.paymentMade : {};
          return {
            id: p._id,
            date: parent.paymentDate || p.appliedDate,
            receiptNo: parent.receiptNumber ?? parent.txnId ?? p.txnId,
            mode: String(parent.paymentMode || ''),
            amount: parseFloat(p.appliedAmountTotal) || 0,
            instrument: parent.instrument,
          };
        });
        rows.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        setPayments(rows);
      })
      .catch(() => { if (active) setPayments([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [dispatch, invoiceId, isPurchase, refreshKey]);

  return { payments, loading };
};

interface InvoicePaymentBreakdownProps {
  payments: InvoicePaymentRow[];
  loading: boolean;
  receivedAmount?: number;
  balanceAmount?: number;
  // Off where the card already shows Received / Balance (receipt preview).
  showTotals?: boolean;
}

// Bottom of the preview details card: one row per payment, then Received / Balance.
const InvoicePaymentBreakdown: FC<InvoicePaymentBreakdownProps> = ({ payments, loading, receivedAmount = 0, balanceAmount = 0, showTotals = true }) => {
  if (loading && payments.length === 0) {
    return <Typography className={BREAKDOWN_CLASS} variant="body2" color="text.secondary">Loading payments…</Typography>;
  }
  if (payments.length === 0) return null;

  const totalRow = (label: string, value: number, color?: string) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', py: 0.25 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color }}>{Utils.formatNumber(value, true, true)}</Typography>
    </Box>
  );

  return (
    <Box className={BREAKDOWN_CLASS}>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Payments</Typography>
      {payments.map(p => (
        <Box key={p.id} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.5, fontSize: '0.875rem' }}>
          <Box sx={{ color: 'text.secondary', minWidth: 84 }}>{Utils.formatDate(p.date)}</Box>
          <Box sx={{ minWidth: 48 }}>{p.receiptNo != null ? `#${p.receiptNo}` : ''}</Box>
          <Box sx={{ flex: 1 }}>
            {p.mode ? modeLabel(p.mode) : '-'}
            {p.instrument && <Typography component="div" variant="caption" color="text.secondary">Txn ID: {p.instrument}</Typography>}
          </Box>
          <Box sx={{ fontWeight: 500 }}>{Utils.formatNumber(p.amount, true, true)}</Box>
        </Box>
      ))}
      {showTotals && (
        <>
          <Divider sx={{ my: 1 }} />
          {totalRow('Received', receivedAmount, 'success.main')}
          {totalRow('Balance', balanceAmount, balanceAmount > 0 ? 'warning.main' : undefined)}
        </>
      )}
    </Box>
  );
};

export default InvoicePaymentBreakdown;
